import { Link } from "@tanstack/react-router";
import { posts } from "@/lib/blog-posts";
import { services } from "@/data/services";

/** "Related articles" block: links to the given blog slugs (unknown slugs are skipped). */
export function RelatedArticles({ slugs, className = "" }: { slugs: string[]; className?: string }) {
  const items = slugs.map((s) => posts.find((p) => p.slug === s)).filter((p) => !!p);
  if (!items.length) return null;
  return (
    <section className={`border-t border-charcoal/10 py-20 ${className}`}>
      <div className="container-page">
        <h2 className="mb-8 text-xs font-medium uppercase tracking-[0.25em] text-moss">Related articles</h2>
        <ul className="grid gap-4 md:grid-cols-3">
          {items.map((p) => (
            <li key={p.slug}>
              <Link to="/blog/$slug" params={{ slug: p.slug }} className="block h-full rounded-sm border border-charcoal/10 bg-background p-6 transition-colors hover:border-moss/40 hover:bg-sage-light/30">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-moss">{p.category}</p>
                <h3 className="mt-3 font-serif text-xl leading-snug text-charcoal">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{p.excerpt}</p>
                <span className="mt-4 inline-block text-xs font-medium uppercase tracking-wide text-moss">Read article →</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** "Related services" block using each service's existing name and short description. */
export function RelatedServices({ slugs, className = "" }: { slugs: string[]; className?: string }) {
  const items = slugs.map((s) => services.find((x) => x.slug === s)).filter((s) => !!s);
  return (
    <section className={`border-t border-charcoal/10 py-20 ${className}`}>
      <div className="container-page">
        <h2 className="mb-8 text-xs font-medium uppercase tracking-[0.25em] text-moss">Related services</h2>
        <ul className={`grid gap-4 md:grid-cols-2 ${items.length > 3 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {items.map((o) => (
            <li key={o.slug}>
              <Link to="/services/$slug" params={{ slug: o.slug }} className="block h-full rounded-sm border border-charcoal/10 bg-background p-6 transition-colors hover:border-moss/40 hover:bg-sage-light/30">
                <p className="font-mono text-[10px] tracking-[0.2em] text-moss">{o.n} / 05</p>
                <h3 className="mt-3 font-serif text-xl text-charcoal">{o.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{o.short}</p>
                <span className="mt-4 inline-block text-xs font-medium uppercase tracking-wide text-moss">Learn more →</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
